import React, { useEffect, useRef, useState } from 'react';
import { useContracts } from '../../../context/ContractContext';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Loader from '../../common/Loader';
import { shortAddr } from '../../../utils/format';
import { useTxRunner } from './AdminHelpers';

export default function OwnerOps({ role }) {
  const { main, mainW } = useContracts();
  const { busy, run } = useTxRunner();
  const [info, setInfo] = useState({});
  const mountedRef = useRef(true);
  const [refresh, setRefresh] = useState(0);

  const [newAdmin, setNewAdmin] = useState('');
  const [newHelper, setNewHelper] = useState('');
  const [newUsdt, setNewUsdt] = useState('');
  const [newToken2, setNewToken2] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [newFee, setNewFee] = useState('');
  const [newDed, setNewDed] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const [own, adm, help, tw, twFee, ded, usdtA, t2A] = await Promise.all([
          main.owner(), main.admin(), main.helper(),
          main.teamWallet(), main.teamWalletFee(), main.claimDeduction(),
          main.usdtToken(), main.token2()
        ]);
        if (!mountedRef.current) return;
        setInfo({
          own, adm, help, tw,
          twFee: twFee.toString(),
          ded: ded.toString(),
          usdtA, t2A
        });
      } catch (_) {}
    })();
    return () => { mountedRef.current = false; };
  }, [main, refresh]);

  const refetch = () => setRefresh((v) => v + 1);
  const send = async (label, fn) => { const ok = await run(label, fn); if (ok) refetch(); };

  const isOwner = role?.isOwner;

  return (
    <>
      {busy && <Loader text={busy} />}

      <Card title="Contract Roles" className="mb-4">
        <div className="grid grid-2">
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>OWNER</div>
            <div className="mono text-primary">{info.own ? shortAddr(info.own) : '—'}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>ADMIN</div>
            <div className="mono text-primary">{info.adm ? shortAddr(info.adm) : '—'}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>HELPER</div>
            <div className="mono text-primary">{info.help ? shortAddr(info.help) : '—'}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>TEAM WALLET</div>
            <div className="mono text-primary">{info.tw ? shortAddr(info.tw) : '—'}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>USDT TOKEN</div>
            <div className="mono text-primary">{info.usdtA ? shortAddr(info.usdtA) : '—'}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>TOKEN2</div>
            <div className="mono text-primary">{info.t2A ? shortAddr(info.t2A) : '—'}</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>TEAM WALLET FEE</div>
            <div className="mono text-warning">{info.twFee} / 10000 ({info.twFee ? (info.twFee / 100).toFixed(2) : '—'}%)</div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>CLAIM DEDUCTION</div>
            <div className="mono text-warning">{info.ded} / 10000 ({info.ded ? (info.ded / 100).toFixed(2) : '—'}%)</div>
          </div>
        </div>
      </Card>

      <div className="grid grid-2 mb-4">
        <Card title="Update Team Wallet">
          <div className="input-group">
            <label className="input-label">New Team Wallet</label>
            <input className="input mono" value={newTeam} onChange={(e) => setNewTeam(e.target.value)} placeholder="0x..." />
          </div>
          <Button block variant="primary" disabled={!newTeam} onClick={() =>
            send('Updating team wallet', () => mainW.updateTeamWallet(newTeam))
          }>UPDATE WALLET</Button>
        </Card>

        <Card title="Update Team Wallet Fee">
          <div className="input-group">
            <label className="input-label">New Fee (basis points, max 10000)</label>
            <input className="input" type="number" value={newFee} onChange={(e) => setNewFee(e.target.value)} placeholder="e.g. 6000 = 60%" />
          </div>
          <Button block variant="warning" disabled={!newFee} onClick={() =>
            send('Updating fee', () => mainW.updateTeamWalletFee(newFee))
          }>UPDATE FEE</Button>
        </Card>
      </div>

      <Card title="Update Claim Deduction" className="mb-4">
        <div className="input-group">
          <label className="input-label">New Deduction (basis points, max 10000)</label>
          <input className="input" type="number" value={newDed} onChange={(e) => setNewDed(e.target.value)} placeholder="e.g. 1000 = 10%" />
        </div>
        <Button block variant="warning" disabled={!newDed} onClick={() =>
          send('Updating deduction', () => mainW.updateClaimDeduction(newDed))
        }>UPDATE DEDUCTION</Button>
      </Card>

      <Card title={isOwner ? 'Owner-Only Actions' : 'Owner-Only Actions (Read-Only)'}>
        {!isOwner && (
          <div className="badge badge-warning mb-4">You are not the owner — these controls are disabled</div>
        )}

        <div className="grid grid-2">
          <div>
            <div className="input-group">
              <label className="input-label">Set Admin Address</label>
              <input className="input mono" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} placeholder="0x..." disabled={!isOwner} />
            </div>
            <Button block variant="primary" disabled={!newAdmin || !isOwner} onClick={() =>
              send('Setting admin', () => mainW.setAdmin(newAdmin))
            }>SET ADMIN</Button>
          </div>

          <div>
            <div className="input-group">
              <label className="input-label">Set Helper Address</label>
              <input className="input mono" value={newHelper} onChange={(e) => setNewHelper(e.target.value)} placeholder="0x..." disabled={!isOwner} />
            </div>
            <Button block variant="primary" disabled={!newHelper || !isOwner} onClick={() =>
              send('Setting helper', () => mainW.setHelper(newHelper))
            }>SET HELPER</Button>
          </div>

          <div>
            <div className="input-group">
              <label className="input-label">Set USDT Token</label>
              <input className="input mono" value={newUsdt} onChange={(e) => setNewUsdt(e.target.value)} placeholder="0x..." disabled={!isOwner} />
            </div>
            <Button block variant="primary" disabled={!newUsdt || !isOwner} onClick={() =>
              send('Setting USDT', () => mainW.setUsdtToken(newUsdt))
            }>SET USDT</Button>
          </div>

          <div>
            <div className="input-group">
              <label className="input-label">Set Token2</label>
              <input className="input mono" value={newToken2} onChange={(e) => setNewToken2(e.target.value)} placeholder="0x..." disabled={!isOwner} />
            </div>
            <Button block variant="primary" disabled={!newToken2 || !isOwner} onClick={() =>
              send('Setting Token2', () => mainW.setToken2(newToken2))
            }>SET TOKEN2</Button>
          </div>
        </div>

        <div className="divider" />

        <div>
          <div className="input-group">
            <label className="input-label text-danger">Transfer Ownership</label>
            <input className="input mono" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} placeholder="0x..." disabled={!isOwner} />
            <div className="input-hint text-danger">⚠ Irreversible. You will lose owner privileges.</div>
          </div>
          <Button block variant="danger" disabled={!newOwner || !isOwner} onClick={() => {
            if (!window.confirm('Transfer ownership? This cannot be undone.')) return;
            send('Transferring ownership', () => mainW.transferOwnership(newOwner));
          }}>TRANSFER OWNERSHIP</Button>
        </div>
      </Card>
    </>
  );
}
